import { useEffect, useState, useMemo, useRef } from 'react';
import { Event, Coordinates } from '@/types/event';
import { fetchEvents, toggleEventInterest, toggleEventSave, fetchNearbyEvents } from '@/api/events';
import EventCard from '@/components/EventCard';
import EventDetailPanel from '@/components/EventDetailPanel';
import { 
  IconSearch, 
  IconFilter, 
  IconX, 
  IconCalendar, 
  IconMapPin,
  IconCategory,
  IconClock,
  IconTag,
} from '@tabler/icons-react';
import styles from './Events.module.css';
import { categoryService, Category } from '../services/categoryService';
import LocationSharing, { LocationSharingRef } from '@/components/LocationSharing';
import { useAuth } from '../auth/hooks/useAuth';

// Options de période
const PERIOD_OPTIONS = [
  { id: 'all', label: 'Toutes les dates' },
  { id: 'today', label: 'Aujourd\'hui' },
  { id: 'tomorrow', label: 'Demain' },
  { id: 'week', label: 'Cette semaine' },
  { id: 'month', label: 'Ce mois-ci' }
];

// Options de distance
const DISTANCE_OPTIONS = [
  { id: 'all', label: 'Toutes les distances' },
  { id: '1', label: 'Moins de 1 km' },
  { id: '5', label: 'Moins de 5 km' },
  { id: '10', label: 'Moins de 10 km' },
  { id: '25', label: 'Moins de 25 km' }
];

// Options de groupage
const GROUPING_OPTIONS = [
  { id: 'date', label: 'Date', icon: IconCalendar },
  { id: 'category', label: 'Catégorie', icon: IconCategory },
];

export function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [maxDistance, setMaxDistance] = useState<string>('all');
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [activePeriod, setActivePeriod] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Grouping option
  const [groupingOption, setGroupingOption] = useState<string>('category');
  
  // Catégories disponibles
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  // Groupement par groupes de catégories pour affichage compact
  const [categoryGroups, setCategoryGroups] = useState<{[key: string]: Category[]}>({});
  
  // Selected event for detail panel
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Référence au composant LocationSharing
  const locationSharingRef = useRef<LocationSharingRef>(null);
  
  // Référence pour suivre si l'effet de connexion récente a déjà été exécuté
  const isRecentLoginExecutedRef = useRef(false);
  
  // Contexte d'authentification 
  const { } = useAuth(); 
  const isRecentLogin = false; 
  
  // Active filters count for badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (activeFilters.length > 0) count++;
    if (activePeriod !== 'all') count++;
    if (maxDistance !== 'all') count++;
    return count;
  }, [activeFilters, activePeriod, maxDistance]);
  
  // Déclaration au niveau du composant - distances stockées globalement
  const distancesMapRef = useRef<Map<number, number>>(new Map());
  
  // Ajouter un nouveau ref pour stocker les événements complets originaux avec distances
  const eventsWithDistancesRef = useRef<Event[]>([]);
  
  // Charger les événements UNIQUEMENT quand la position est disponible
  const [loadingPosition, setLoadingPosition] = useState(true);
  const [positionDenied, setPositionDenied] = useState(false);
  const positionLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fonction pour obtenir la couleur d'une catégorie
  const getCategoryColor = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.color || '#ADB5BD';
  };

  // Fonction pour formater des distances et les stocker dans une Map globale
  const formatAndCacheDistance = (eventId: number, distance: number | undefined): string => {
    // Flag for debugging - set to true only when debugging distance issues
    const DEBUG_DISTANCE = false;
    
    // Si la distance est définie, la stocker dans la Map
    if (typeof distance === 'number' && !isNaN(distance)) {
      distancesMapRef.current.set(eventId, distance);
    }
    
    // Utiliser la distance de l'événement ou récupérer celle stockée dans la Map
    const effectiveDistance = (typeof distance === 'number' && !isNaN(distance)) 
      ? distance 
      : distancesMapRef.current.get(eventId);

    if (effectiveDistance === undefined || effectiveDistance === null || isNaN(effectiveDistance)) {
      return '';
    }
    
    // Only log when debugging is enabled
    if (DEBUG_DISTANCE) {
      console.log(`📏 Formatage de distance pour l'événement #${eventId}:`, effectiveDistance);
    }
    
    // Moins de 1 km: afficher en mètres
    if (effectiveDistance < 1) {
      return `${Math.round(effectiveDistance * 1000)}m`;
    }
    
    // Entre 1 et 10 km: 1 décimale
    if (effectiveDistance < 10) {
      return `${effectiveDistance.toFixed(1)}km`;
    }
    
    // Plus de 10 km: nombre entier
    return `${Math.round(effectiveDistance)}km`;
  };
  
  // Charger les données initiales
  const loadInitialData = () => {
    console.log('🔄 Chargement initial des données...');
    loadCategories();
    
    // Gérer la localisation
    if (navigator.geolocation) {
      setLoadingPosition(true);
      
      // Définir un timeout pour éviter de bloquer complètement si la position n'est pas disponible
      positionLoadTimeoutRef.current = setTimeout(() => {
        console.log('⏱️ Timeout de récupération de la position, chargement sans position...');
        setLoadingPosition(false);
        setPositionDenied(true);
        loadEventsWithoutPosition();
      }, 3000); // 3 secondes de timeout maximum pour la position
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (positionLoadTimeoutRef.current) {
            clearTimeout(positionLoadTimeoutRef.current);
          }
          
          // Mettre à jour la position et charger les événements à proximité
          const coords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          setUserLocation(coords);
          handleLocationUpdate(coords);
          setLoadingPosition(false);
        },
        (error) => {
          console.error('❌ Erreur de géolocalisation:', error);
          if (positionLoadTimeoutRef.current) {
            clearTimeout(positionLoadTimeoutRef.current);
          }
          
          setPositionDenied(true);
          setLoadingPosition(false);
          loadEventsWithoutPosition();
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      // Navigateur ne supporte pas la géolocalisation
      console.log('📍 Géolocalisation non supportée par le navigateur');
      setPositionDenied(true);
      setLoadingPosition(false);
      loadEventsWithoutPosition();
    }
  };

  // Charger les événements sans position
  const loadEventsWithoutPosition = async () => {
    console.log('🔄 Chargement des événements sans position...');
    setLoading(true);
    
    try {
      const allEvents = await fetchEvents();
      console.log(`✅ ${allEvents.length} événements récupérés sans position`);
      setEvents(allEvents);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des événements:', err);
      setError('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les catégories
  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const availableCategories = await categoryService.getCategories();
      setCategories(availableCategories);
      
      // Organiser les catégories par groupes pour un affichage plus compact
      const groups: {[key: string]: Category[]} = {};
      availableCategories.forEach(category => {
        if (!category.subcategories || category.subcategories.length === 0) {
          if (!groups['general']) groups['general'] = [];
          groups['general'].push(category);
        } else {
          groups[category.id] = [category, ...(category.subcategories || [])];
        }
      });
      setCategoryGroups(groups);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Charger les événements à proximité
  const loadNearbyEvents = async (coords: Coordinates) => {
    try {
      setLoading(true);
      
      console.log('📍 Chargement des événements à proximité avec coordonnées:', coords);
      
      // S'assurer que les coordonnées sont valides
      if (!coords || typeof coords.lat !== 'number' || typeof coords.lon !== 'number') {
        console.error('❌ Coordonnées invalides pour chargement des événements:', coords);
        // Charger les événements standards si les coordonnées sont invalides
        const defaultEvents = await fetchEvents();
        setEvents(defaultEvents);
        return;
      }
      
      // Utiliser une distance plus grande pour s'assurer d'obtenir tous les événements pertinents
      const eventsWithDistance = await fetchNearbyEvents(coords, 100);
      console.log(`✅ ${eventsWithDistance.length} événements chargés avec distances`);
      
      // Vérifier si les événements ont bien leurs distances
      const hasDistances = eventsWithDistance.some(event => typeof event.distance === 'number');
      if (!hasDistances) {
        console.warn('⚠️ Les événements retournés n\'ont pas de distances calculées');
      } else {
        // Sauvegarder toutes les distances dans la map globale
        eventsWithDistance.forEach(event => {
          if (typeof event.distance === 'number' && !isNaN(event.distance)) {
            console.log(`💾 Mise en cache de la distance pour l'événement #${event.id}:`, event.distance);
            distancesMapRef.current.set(event.id, event.distance);
          }
        });
        
        // Sauvegarder immédiatement ces événements dans la référence
        eventsWithDistancesRef.current = [...eventsWithDistance];
      }
      
      setEvents(eventsWithDistance);
    } catch (err) {
      console.error('❌ Erreur de chargement des événements proximité:', err);
      // En cas d'erreur, essayer de charger les événements standards
      try {
        const fallbackEvents = await fetchEvents();
        setEvents(fallbackEvents);
      } catch (fallbackErr) {
        console.error('❌ Échec du chargement de secours des événements:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle location updates
  const handleLocationUpdate = (coords: Coordinates) => {
    setUserLocation(coords);
    console.log('📍 Position utilisateur mise à jour:', coords);
    // Charger les événements à proximité avec la nouvelle position
    loadNearbyEvents(coords);
  };

  // Handle distance filter change
  const handleDistanceChange = (distanceId: string) => {
    setMaxDistance(distanceId);
  };

  // Toggle a filter selection with synchronized selectedCategories
  const toggleFilter = (categoryId: string) => {
    console.log('Toggling filter for category ID:', categoryId);
    setActiveFilters(prev => {
      if (prev.includes(categoryId)) {
        setSelectedCategories(current => current.filter(id => id !== categoryId));
        return prev.filter(c => c !== categoryId);
      } else {
        setSelectedCategories(current => [...current, categoryId]);
        return [...prev, categoryId];
      }
    });
  };

  // Handle interest click
  const handleInterestClick = async (eventId: number) => {
    try {
      await toggleEventInterest(eventId);
      
      // Maintenir les distances entre les mises à jour
      setEvents(prev => {
        return prev.map(e => {
          if (e.id === eventId) {
            // Trouver la version avec distance de cet événement s'il existe
            const eventWithDistance = eventsWithDistancesRef.current.find(ed => ed.id === eventId);
            // Créer un nouvel objet qui combine les propriétés actuelles, la distance stockée, et met à jour isInterested
            return { 
              ...e, 
              isInterested: !e.isInterested,
              // Restaurer la distance si elle existe dans la référence mais pas dans l'événement actuel
              distance: (typeof e.distance === 'number') ? e.distance : (eventWithDistance?.distance || e.distance)
            };
          }
          return e;
        });
      });
      
      // Update the selected event if it's the one that was toggled
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => prev ? { ...prev, isInterested: !prev.isInterested } : null);
      }
    } catch (err) {
      console.error('Error toggling interest:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  // Handle save click
  const handleSaveClick = async (eventId: number) => {
    try {
      await toggleEventSave(eventId);
      
      // Maintenir les distances entre les mises à jour
      setEvents(prev => {
        return prev.map(e => {
          if (e.id === eventId) {
            // Trouver la version avec distance de cet événement s'il existe
            const eventWithDistance = eventsWithDistancesRef.current.find(ed => ed.id === eventId);
            // Créer un nouvel objet qui combine les propriétés actuelles, la distance stockée, et met à jour isSaved
            return { 
              ...e, 
              isSaved: !e.isSaved,
              // Restaurer la distance si elle existe dans la référence mais pas dans l'événement actuel
              distance: (typeof e.distance === 'number') ? e.distance : (eventWithDistance?.distance || e.distance)
            };
          }
          return e;
        });
      });
      
      // Update the selected event if it's the one that was toggled
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(prev => prev ? { ...prev, isSaved: !prev.isSaved } : null);
      }
    } catch (err) {
      console.error('Error toggling save:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePeriodChange = (periodId: string) => {
    setActivePeriod(periodId);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setActiveFilters([]);
    setActivePeriod('all');
    setMaxDistance('all');
  };

  // Remove a specific filter
  const removeFilter = (filterId: string) => {
    if (PERIOD_OPTIONS.some(option => option.id === filterId)) {
      setActivePeriod('all');
    } else if (DISTANCE_OPTIONS.some(option => option.id === filterId)) {
      setMaxDistance('all');
    } else {
      setActiveFilters(prev => prev.filter(f => f !== filterId));
    }
  };

  // Helper to check if a date is within the selected period
  const isDateInPeriod = (dateString: string): boolean => {
    if (activePeriod === 'all') return true;
    
    const eventDate = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time part for comparison
    const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    
    switch (activePeriod) {
      case 'today':
        return eventDay.getTime() === today.getTime();
      case 'tomorrow':
        return eventDay.getTime() === tomorrow.getTime();
      case 'week': {
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
        return eventDay >= today && eventDay <= endOfWeek;
      }
      case 'month': {
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return eventDay >= today && eventDay <= endOfMonth;
      }
      default:
        return true;
    }
  };

  // Filtrer et grouper les événements
  const filteredAndGroupedEvents = useMemo(() => {
    // Fonction qui détermine si un événement correspond aux filtres actuels
    const matchesFilters = (event: Event) => {
      // Filtre sur la recherche
      if (searchTerm && !event.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
         !(event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)) {
        return false;
      }
      
      // Filtre sur les catégories
      if (activeFilters.length > 0 && !(
        activeFilters.includes(event.eventCategory?.id || '') ||
        activeFilters.includes(event.eventCategory?.code || '')
      )) {
        return false;
      }
      
      // Filtre sur la période
      if (activePeriod !== 'all' && !isDateInPeriod(event.dateTime)) {
        return false;
      }
      
      // Filtre sur la distance (si la localisation est activée)
      if (userLocation && maxDistance !== 'all') {
        const eventDistance = (event as any).distance; // Accéder à la distance calculée par le backend
        if (typeof eventDistance === 'number') {
          const maxDistanceValue = parseInt(maxDistance, 10);
          if (eventDistance > maxDistanceValue) {
            return false;
          }
        }
      }
      
      return true;
    };
    
    // First, filter events based on search term, active filters, and period
    const filtered = events.filter(matchesFilters);
    
    // Group by selected grouping option
    if (groupingOption === 'date') {
      // Group by date with proper sorting
      const groupedByDate: Record<string, Event[]> = {};
      
      // Pour stocker les dates associées à chaque jour formaté (pour trier correctement par la suite)
      const dayDates: Record<string, Date> = {};
      
      // Grouper les événements par jour
      filtered.forEach(event => {
        const eventDate = new Date(event.dateTime);
        const day = eventDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        
        if (!groupedByDate[day]) {
          groupedByDate[day] = [];
          // Stocker la date sans l'heure pour chaque groupe
          dayDates[day] = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        }
        
        groupedByDate[day].push(event);
      });
      
      // Trier les événements par heure dans chaque groupe
      Object.keys(groupedByDate).forEach(day => {
        groupedByDate[day].sort((a, b) => {
          const dateA = new Date(a.dateTime);
          const dateB = new Date(b.dateTime);
          return dateA.getTime() - dateB.getTime();
        });
      });
      
      // Créer un objet trié par ordre chronologique des jours
      const sortedGroupedByDate: Record<string, Event[]> = {};
      
      // Trier les jours dans l'ordre chronologique
      Object.keys(groupedByDate)
        .sort((dayA, dayB) => dayDates[dayA].getTime() - dayDates[dayB].getTime())
        .forEach(day => {
          sortedGroupedByDate[day] = groupedByDate[day];
        });
      
      return {
        filteredCount: filtered.length,
        totalCount: events.length,
        groupedByDay: sortedGroupedByDate,
        groupedByCategory: null
      };
    } else {
      // Group by category ID with name for display
      const groupedByCategory = filtered.reduce((acc, event) => {
        if (!event.eventCategory) {
          // Handle events without category
          if (!acc['other']) {
            acc['other'] = {
              id: 'other',
              name: 'Autre',
              color: '#ADB5BD',
              events: []
            };
          }
          acc['other'].events.push(event);
          return acc;
        }
        
        // Use category ID as key for consistency with filtering
        const categoryId = String(event.eventCategory.id);
        
        if (!acc[categoryId]) {
          acc[categoryId] = {
            id: categoryId,
            name: event.eventCategory.name,
            color: event.eventCategory.color || '#ADB5BD',
            events: []
          };
        }
        
        acc[categoryId].events.push(event);
        return acc;
      }, {} as Record<string, {id: string, name: string, color: string, events: Event[]}>);
      
      return {
        filteredCount: filtered.length,
        totalCount: events.length,
        groupedByDay: null,
        groupedByCategory: groupedByCategory
      };
    }
  }, [events, searchTerm, activeFilters, activePeriod, groupingOption, userLocation, maxDistance]);

  // Handle grouping option change
  const handleGroupingChange = (option: string) => {
    setGroupingOption(option);
  };

  // Get active filter labels for display
  const activeFilterLabels = useMemo(() => {
    const labels: { id: string, label: string, type: 'category' | 'period' | 'distance' }[] = [];
    
    // Add category filters
    activeFilters.forEach(filterId => {
      // Find the category matching this ID
      const category = categories.find(c => c.id === filterId);
      if (category) {
        labels.push({ id: filterId, label: category.name, type: 'category' });
      } else {
        // Check subcategories
        for (const cat of categories) {
          if (cat.subcategories) {
            const subcat = cat.subcategories.find((sc: Category) => sc.id === filterId);
            if (subcat) {
              labels.push({ id: filterId, label: subcat.name, type: 'category' });
              break;
            }
          }
        }
      }
    });
    
    // Add period filter
    if (activePeriod !== 'all') {
      const periodOption = PERIOD_OPTIONS.find(p => p.id === activePeriod);
      if (periodOption) {
        labels.push({ id: activePeriod, label: periodOption.label, type: 'period' });
      }
    }
    
    // Add distance filter
    if (maxDistance !== 'all') {
      const distanceOption = DISTANCE_OPTIONS.find(d => d.id === maxDistance);
      if (distanceOption) {
        labels.push({ id: maxDistance, label: distanceOption.label, type: 'distance' });
      }
    }
    
    return labels;
  }, [activeFilters, activePeriod, maxDistance, categories]);

  // Handle clicking on an event card
  const handleEventCardClick = (event: Event) => {
    setSelectedEvent(event);
  };

  // Close the detail panel
  const handleCloseDetails = () => {
    setSelectedEvent(null);
  };

  // Détecter une connexion récente et actualiser la position automatiquement
  useEffect(() => {
    // Si l'utilisateur vient de se connecter, déclencher l'actualisation de position
    if (isRecentLogin && !isRecentLoginExecutedRef.current) {
      // Marquer cet effet comme déjà exécuté
      isRecentLoginExecutedRef.current = true;
      
      console.log('🔐 Connexion récente détectée, actualisation automatique...');
      
      // Réinitialiser immédiatement le flag pour éviter une double exécution
      // setIsRecentLogin(false);
      
      // Léger délai pour s'assurer que le composant LocationSharing est bien monté
      const timer = setTimeout(() => {
        // Tenter d'utiliser la référence pour actualiser la position
        if (locationSharingRef.current) {
          console.log('📍 Déclenchement de la géolocalisation...');
          locationSharingRef.current.updateLocation();
        } else {
          // Sinon, déclencher directement la géolocalisation
          console.log('⚠️ Référence non disponible, fallback direct...');
          navigator.geolocation.getCurrentPosition(
            (position) => {
              handleLocationUpdate({
                lat: position.coords.latitude,
                lon: position.coords.longitude
              });
            },
            (error) => {
              console.error('❌ Erreur d\'actualisation de position:', error);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isRecentLogin]);

  // Effet pour initialiser les données au chargement de la page
  useEffect(() => {
    loadInitialData();
    
    // Cleanup function
    return () => {
      if (positionLoadTimeoutRef.current) {
        clearTimeout(positionLoadTimeoutRef.current);
      }
    };
  }, []); // Exécuter une seule fois au chargement

  return (
    <div className={styles.eventsPage}>
      {/* Header avec search et filtres... */}
      <header className={styles.header}>
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <IconSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Rechercher un événement..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button className={styles.clearButton} onClick={() => setSearchTerm('')}>
                <IconX size={14} />
              </button>
            )}
          </div>
          
          <button 
            className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <IconFilter size={16} />
            <span>{activeFiltersCount > 0 ? `Filtres (${activeFiltersCount})` : 'Filtres'}</span>
          </button>
          
          <LocationSharing 
            onLocationUpdate={handleLocationUpdate} 
            ref={locationSharingRef}
            showMap={false}
            forcePrompt={!userLocation}
          />
        </div>
        
        {/* Active filter tags */}
        {activeFilterLabels.length > 0 && (
          <div className={styles.activeFilterTags}>
            {activeFilterLabels.map(filter => (
              <div key={filter.id} className={styles.filterTag}>
                {filter.type === 'category' && <IconTag size={12} />}
                {filter.type === 'period' && <IconClock size={12} />}
                {filter.type === 'distance' && <IconMapPin size={12} />}
                <span>{filter.label}</span>
                <button onClick={() => removeFilter(filter.id)}>
                  <IconX size={10} />
                </button>
              </div>
            ))}
            {activeFilterLabels.length > 1 && (
              <button className={styles.resetFilters} onClick={clearFilters}>
                Tout effacer
              </button>
            )}
          </div>
        )}
        
        {/* Extended filters panel */}
        {showFilters && (
          <div className={styles.filtersPanel}>
            {/* Period filter */}
            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <IconCalendar size={18} />
                <span>Période</span>
              </div>
              <div className={styles.filterOptions}>
                {PERIOD_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    className={`${styles.filterOption} ${activePeriod === option.id ? styles.active : ''}`}
                    onClick={() => handlePeriodChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Distance filter - only shown when user has shared location */}
            {userLocation && (
              <div className={styles.filterSection}>
                <div className={styles.filterLabel}>
                  <IconMapPin size={18} />
                  <span>Distance</span>
                </div>
                <div className={styles.filterOptions}>
                  {DISTANCE_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      className={`${styles.filterOption} ${maxDistance === option.id ? styles.active : ''}`}
                      onClick={() => handleDistanceChange(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Categories filter - version compacte */}
            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <IconCategory size={18} />
                <span>Catégories</span>
              </div>
              
              {categoriesLoading ? (
                <div className={styles.loading}>Chargement des catégories...</div>
              ) : (
                <div className={styles.categoryCompactView}>
                  {Object.entries(categoryGroups).map(([_, groupCategories]) => (
                    groupCategories.map(category => (
                      <button
                        key={category.id}
                        className={`${styles.categoryBadge} ${selectedCategories.includes(category.id) ? styles.active : ''}`}
                        onClick={() => toggleFilter(category.id)}
                        style={{
                          "--category-color": getCategoryColor(category.id)
                        } as React.CSSProperties}
                      >
                        <span className={styles.categoryIndicator}></span>
                        {category.name}
                      </button>
                    ))
                  ))}
                </div>
              )}
            </div>
            
            {/* Grouping option */}
            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <IconCalendar size={18} />
                <span>Groupage</span>
              </div>
              <div className={styles.filterOptions}>
                {GROUPING_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    className={`${styles.filterOption} ${groupingOption === option.id ? styles.active : ''}`}
                    onClick={() => handleGroupingChange(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Reset all filters */}
            <button className={styles.resetFilters} onClick={clearFilters}>
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </header>

      <main className={styles.content}>
        {/* All events section */}
        <section className={styles.allEventsSection}>
          <h2 className={styles.sectionTitle}>Tous les événements</h2>
          {loading ? (
            <div className={styles.loading}>Chargement des événements...</div>
          ) : loadingPosition ? (
            <div className={styles.loading}>
              <p>En attente de votre position pour afficher les événements les plus proches...</p>
              <p className={styles.loadingHint}>Ceci permet d'afficher les distances précises pour chaque événement</p>
              <button 
                className={styles.skipLocationButton}
                onClick={() => {
                  // L'utilisateur veut explicitement charger sans position
                  if (positionLoadTimeoutRef.current) {
                    clearTimeout(positionLoadTimeoutRef.current);
                    positionLoadTimeoutRef.current = null;
                  }
                  setPositionDenied(true);
                  setLoadingPosition(false);
                  loadEventsWithoutPosition();
                }}
              >
                Continuer sans localisation
              </button>
            </div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : filteredAndGroupedEvents.filteredCount === 0 ? (
            <div className={styles.emptyState}>
              <p>Aucun événement ne correspond à vos critères.</p>
              <button className={styles.resetFilters} onClick={clearFilters}>
                Réinitialiser les filtres
              </button>
            </div>
          ) : (
            <>
              <div className={styles.resultCount}>
                {filteredAndGroupedEvents.filteredCount} événements sur {filteredAndGroupedEvents.totalCount}
              </div>
              
              {filteredAndGroupedEvents.groupedByDay && (
                Object.entries(filteredAndGroupedEvents.groupedByDay).map(([day, dayEvents]) => (
                  <div key={day} className={styles.dayGroup}>
                    <h3 className={styles.dayHeader}>{day}</h3>
                    <div className={styles.eventsList}>
                      {dayEvents.map(event => (
                        <EventCard 
                          key={event.id}
                          event={event}
                          onClick={() => handleEventCardClick(event)}
                          onInterestClick={() => handleInterestClick(event.id)}
                          onSaveClick={() => handleSaveClick(event.id)}
                          distanceLabel={formatAndCacheDistance(event.id, event.distance)}
                          className={styles.gridCard}
                          showStatus={false}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
              
              {filteredAndGroupedEvents.groupedByCategory && (
                Object.entries(filteredAndGroupedEvents.groupedByCategory).map(([categoryId, categoryData]) => {
                  return (
                    <div key={categoryId} className={styles.categoryGroup}>
                      <h3 className={styles.categoryHeader}>
                        <span 
                          className={styles.categoryIndicator} 
                          style={{ backgroundColor: categoryData.color }}
                        ></span>
                        {categoryData.name} {/* Display name but use ID for filtering */}
                      </h3>
                      <div className={styles.eventsList}>
                        {categoryData.events.map(event => (
                          <EventCard 
                            key={event.id}
                            event={event}
                            onClick={() => handleEventCardClick(event)}
                            onInterestClick={() => handleInterestClick(event.id)}
                            onSaveClick={() => handleSaveClick(event.id)}
                            distanceLabel={formatAndCacheDistance(event.id, event.distance)}
                            className={styles.gridCard}
                            showStatus={false}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </section>
      </main>

      {/* Event detail panel */}
      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          onClose={handleCloseDetails}
          onInterestClick={() => handleInterestClick(selectedEvent.id)}
          onSaveClick={() => handleSaveClick(selectedEvent.id)}
          userLocation={userLocation}
          distanceLabel={userLocation && ((selectedEvent as any).distance || distancesMapRef.current.get(selectedEvent.id))
            ? formatAndCacheDistance(selectedEvent.id, (selectedEvent as any).distance || distancesMapRef.current.get(selectedEvent.id))
            : undefined}
        />
      )}
      
      {positionDenied && (
        <div className={styles.positionWarning}>
          <p>⚠️ Localisation non disponible. Les distances entre vous et les événements ne peuvent pas être affichées.</p>
          <button 
            className={styles.enableLocationButton}
            onClick={() => {
              // Tenter de demander à nouveau la position
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  handleLocationUpdate({
                    lat: position.coords.latitude,
                    lon: position.coords.longitude
                  });
                  setPositionDenied(false);
                },
                (error) => {
                  console.error('❌ Erreur d\'actualisation de position:', error);
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
              );
            }}
          >
            Activer la localisation
          </button>
        </div>
      )}
    </div>
  );
}
